const imageExtensions = /\.(avif|gif|jpe?g|png|webp)$/i;
const galleryScopeSelector = ".single-gallery-grid, .wp-block-gallery, .gallery";
const explicitLightboxTriggerSelector = ".ac-image-lightbox-trigger";
const lightboxTriggerSelector = `${explicitLightboxTriggerSelector}, a[href]`;
const lightboxIgnoreSelector = "[data-no-lightbox], .attachments-section a";

let lightboxLinksBound = false;

const cleanText = (text = "") => String(text || "").replace(/\s+/g, " ").trim();

const shouldIgnoreClick = (event) =>
  event.defaultPrevented ||
  event.button !== 0 ||
  event.altKey ||
  event.ctrlKey ||
  event.metaKey ||
  event.shiftKey;

const getLargestSrcsetUrl = (srcset = "") =>
  srcset
    .split(",")
    .map((source) => {
      const [url, descriptor = ""] = source.trim().split(/\s+/);

      return {
        url,
        width: parseInt(descriptor.replace("w", ""), 10) || 0,
      };
    })
    .filter(({ url }) => url)
    .sort((a, b) => b.width - a.width)[0]?.url || "";

const normalizeImageUrl = (src = "") => {
  if (!src || src.startsWith("#")) {
    return "";
  }

  try {
    const url = new URL(src, window.location.href);

    if (!imageExtensions.test(url.pathname)) {
      return "";
    }

    return url.href;
  } catch (error) {
    return "";
  }
};

const firstImageUrl = (...sources) =>
  sources.map(normalizeImageUrl).find(Boolean) || "";

const getNestedImageSrc = (link) => {
  const image = link.querySelector("img");

  if (!image) {
    return "";
  }

  return firstImageUrl(
    getLargestSrcsetUrl(image.getAttribute("srcset") || ""),
    image.currentSrc,
    image.getAttribute("src")
  );
};

const getHrefImageSrc = (link) =>
  firstImageUrl(
    link.getAttribute("data-lightbox-src"),
    link.getAttribute("href")
  );

const allowsNestedImageSrc = (link) =>
  link.matches(explicitLightboxTriggerSelector) ||
  Boolean(link.closest(galleryScopeSelector));

const getImageSrc = (link) =>
  getHrefImageSrc(link) ||
  (allowsNestedImageSrc(link) ? getNestedImageSrc(link) : "");

const isLightboxLink = (link) => {
  if (link.hasAttribute("download") || link.closest(lightboxIgnoreSelector)) {
    return false;
  }

  return getImageSrc(link) !== "";
};

const getLightboxLinks = (scope) =>
  Array.from(scope.querySelectorAll(lightboxTriggerSelector)).filter(
    isLightboxLink
  );

const getRelGalleryName = (link) => {
  const galleryName = link.getAttribute("data-lightbox-gallery");

  if (galleryName) {
    return galleryName;
  }

  const rel = link.getAttribute("rel");

  if (!rel) {
    return "";
  }

  return (
    rel
      .split(/\s+/)
      .find((token) => token === "gallery" || token.startsWith("gallery[")) ||
    ""
  );
};

const getGalleryLinks = (link) => {
  const scopedGallery = link.closest(galleryScopeSelector);

  if (scopedGallery) {
    return getLightboxLinks(scopedGallery);
  }

  const relGalleryName = getRelGalleryName(link);

  if (relGalleryName) {
    return getLightboxLinks(document).filter(
      (relLink) => getRelGalleryName(relLink) === relGalleryName
    );
  }

  return [link];
};

const getItem = (link) => {
  const image = link.querySelector("img");
  const caption =
    link.querySelector(".gallery-caption") ||
    link.closest(".gallery-item")?.querySelector(".gallery-caption");

  return {
    alt: cleanText(
      image?.getAttribute("alt") ||
        image?.getAttribute("title") ||
        link.getAttribute("aria-label") ||
        link.getAttribute("title")
    ),
    caption: cleanText(
      link.getAttribute("data-caption") ||
        link.getAttribute("data-lightbox-caption") ||
        caption?.textContent ||
        image?.getAttribute("alt") ||
        link.getAttribute("title")
    ),
    src: getImageSrc(link),
  };
};

const getGallery = (link) => {
  const links = getGalleryLinks(link);
  const items = links.map((galleryLink) => getItem(galleryLink));
  const index = Math.max(
    links.findIndex((galleryLink) => galleryLink === link),
    0
  );

  return {
    index,
    items,
  };
};

export const bindImageLightboxLinks = () => {
  if (lightboxLinksBound) {
    return;
  }

  lightboxLinksBound = true;

  const handleLightboxClick = (event) => {
    if (shouldIgnoreClick(event)) {
      return;
    }

    const target =
      event.target instanceof Element
        ? event.target
        : event.target.parentElement;
    const link = target?.closest(lightboxTriggerSelector);

    if (!link || !isLightboxLink(link)) {
      return;
    }

    const gallery = getGallery(link);

    if (!gallery.items.length) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    window.dispatchEvent(
      new CustomEvent("ac-image-lightbox", {
        detail: {
          index: gallery.index,
          items: gallery.items,
          trigger: link,
        },
      })
    );
  };

  window.addEventListener("click", handleLightboxClick, true);
};

export default () => ({
  currentIndex: 0,
  isOpen: false,
  items: [],
  lastFocusedElement: null,

  get activeItem() {
    return (
      this.items[this.currentIndex] || {
        alt: "",
        caption: "",
        src: "",
      }
    );
  },

  get hasMultipleItems() {
    return this.items.length > 1;
  },

  openFromEvent(event) {
    this.open(event.detail.items, event.detail.index, event.detail.trigger);
  },

  open(items, index, trigger) {
    this.items = items;
    this.currentIndex = index;
    this.lastFocusedElement = trigger;
    this.isOpen = true;
  },

  close() {
    this.isOpen = false;
    this.items = [];
    this.currentIndex = 0;

    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
      this.lastFocusedElement = null;
    }
  },

  next() {
    if (!this.isOpen || !this.hasMultipleItems) {
      return;
    }

    this.currentIndex = (this.currentIndex + 1) % this.items.length;
  },

  previous() {
    if (!this.isOpen || !this.hasMultipleItems) {
      return;
    }

    this.currentIndex =
      (this.currentIndex - 1 + this.items.length) % this.items.length;
  },
});
